/**
 * reportGenerators.js
 * Utility functions for generating PDF and Excel reports
 * Uses: jspdf + jspdf-autotable (PDF), xlsx/SheetJS (Excel)
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// ── Formatters ─────────────────────────────────────────────────────────────────
const fmt = (v) =>
  `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = (v) => Number(v || 0).toLocaleString('it-IT');
const fmtQ = (v, d = 4) => v == null ? '—' : Number(v).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: d });
const pct = (cur, prev) =>
  prev > 0 ? `${((cur - prev) / prev * 100).toFixed(1)}%` : 'N/D';
const ALERT_IT = { red: 'Sotto scorta', yellow: 'Scorta bassa', green: 'OK' };

const STATUS_IT = {
  draft: 'Bozza', confirmed: 'Confermato', processing: 'In Lavorazione',
  shipped: 'Spedito', delivered: 'Consegnato', cancelled: 'Annullato',
};
const INV_STATUS_IT = {
  draft: 'Bozza', sent: 'Inviata', paid: 'Pagata', overdue: 'Scaduta', cancelled: 'Annullata',
};

// ── Primary colors ─────────────────────────────────────────────────────────────
const GREEN  = [22, 163, 74];
const DARK   = [30, 41, 59];
const LIGHT  = [248, 250, 252];
const BORDER = [226, 232, 240];

// ── PDF helpers ────────────────────────────────────────────────────────────────

function addHeader(doc, title, subtitle) {
  // Green top bar
  doc.setFillColor(...GREEN);
  doc.rect(0, 0, 210, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('AGRI-CRM', 10, 9.5);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Report generato il ${new Date().toLocaleDateString('it-IT')}`, 200, 9.5, { align: 'right' });

  // Title
  doc.setTextColor(...DARK);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 10, 24);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(subtitle, 10, 30);

  return 38; // y position after header
}

function sectionTitle(doc, text, y) {
  doc.setFillColor(...LIGHT);
  doc.setDrawColor(...BORDER);
  doc.roundedRect(10, y, 190, 7, 1, 1, 'FD');
  doc.setTextColor(...GREEN);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(text.toUpperCase(), 13, y + 4.8);
  return y + 10;
}

function autoT(doc, { head, body, startY, ...rest }) {
  autoTable(doc, {
    head,
    body,
    startY,
    styles: { fontSize: 8, cellPadding: 2.5, textColor: DARK },
    headStyles: { fillColor: DARK, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5 },
    alternateRowStyles: { fillColor: LIGHT },
    margin: { left: 10, right: 10 },
    tableWidth: 190,
    ...rest,
  });
  return doc.lastAutoTable.finalY + 6;
}

// ── Build sections ─────────────────────────────────────────────────────────────

function buildKpiSection(doc, annualData, prevData, mode, y) {
  y = sectionTitle(doc, 'Riepilogo KPI', y);
  const s = annualData.summary;
  const p = prevData?.summary || {};
  const yr = annualData.year;
  const pyr = yr - 1;

  const head = mode === 'comparison'
    ? [['Indicatore', `Anno ${yr}`, `Anno ${pyr}`, 'Variazione']]
    : [['Indicatore', `Anno ${yr}`]];

  const rows = [
    ['Totale Ordini', fmtN(s.total_orders), ...(mode === 'comparison' ? [fmtN(p.total_orders || 0), pct(s.total_orders, p.total_orders || 0)] : [])],
    ['Fatturato (IVA incl.)', fmt(s.total_revenue), ...(mode === 'comparison' ? [fmt(p.total_revenue || 0), pct(s.total_revenue, p.total_revenue || 0)] : [])],
    ['Imponibile Netto', fmt(s.net_revenue), ...(mode === 'comparison' ? [fmt(p.net_revenue || 0), pct(s.net_revenue, p.net_revenue || 0)] : [])],
    ['IVA Totale', fmt(s.tax_amount), ...(mode === 'comparison' ? [fmt(p.tax_amount || 0), pct(s.tax_amount, p.tax_amount || 0)] : [])],
    ['Valore Medio Ordine', fmt(s.avg_order_value), ...(mode === 'comparison' ? [fmt(p.avg_order_value || 0), pct(s.avg_order_value, p.avg_order_value || 0)] : [])],
  ];

  return autoT(doc, { head, body: rows, startY: y });
}

function buildMonthlySection(doc, annualData, prevData, mode, y) {
  y = sectionTitle(doc, `Andamento Mensile — Anno ${annualData.year}`, y);
  const months = annualData.monthly_trend || [];

  const head = mode === 'comparison'
    ? [['Mese', `Fatturato ${annualData.year}`, `Imponibile ${annualData.year}`, `Ordini ${annualData.year}`, `Fatturato ${annualData.year - 1}`, `Ordini ${annualData.year - 1}`]]
    : [['Mese', 'Fatturato', 'Imponibile', 'Ordini']];

  const prevMonths = prevData?.monthly_trend || [];
  const rows = months.map((m, i) => {
    const pm = prevMonths[i] || {};
    return mode === 'comparison'
      ? [m.month, fmt(m.revenue), fmt(m.net_revenue), m.orders, fmt(pm.revenue || 0), pm.orders || 0]
      : [m.month, fmt(m.revenue), fmt(m.net_revenue), m.orders];
  });

  return autoT(doc, { head, body: rows, startY: y });
}

function buildStatusSection(doc, annualData, y) {
  y = sectionTitle(doc, `Ordini per Stato — Anno ${annualData.year}`, y);
  const status = annualData.summary?.by_status || {};
  const rows = Object.entries(status)
    .sort((a, b) => b[1].value - a[1].value)
    .map(([k, v]) => [STATUS_IT[k] || k, fmtN(v.count), fmt(v.value)]);

  return autoT(doc, {
    head: [['Stato', 'Ordini', 'Fatturato']],
    body: rows, startY: y,
    columnStyles: { 0: { cellWidth: 50 }, 1: { halign: 'right' }, 2: { halign: 'right' } },
  });
}

function buildClientsSection(doc, annualData, prevData, mode, y) {
  y = sectionTitle(doc, `Top Clienti — Anno ${annualData.year}`, y);
  const clients = annualData.top_clients || [];
  const prevMap = {};
  (prevData?.top_clients || []).forEach(c => { prevMap[c.name] = c; });

  const head = mode === 'comparison'
    ? [['#', 'Cliente', `Ordini ${annualData.year}`, `Fatturato ${annualData.year}`, `Ordini ${annualData.year - 1}`, `Fatturato ${annualData.year - 1}`]]
    : [['#', 'Cliente', 'Ordini', 'Fatturato', 'Imponibile']];

  const rows = clients.map((c, i) => {
    const p = prevMap[c.name] || {};
    return mode === 'comparison'
      ? [i + 1, c.name, c.orders, fmt(c.revenue), p.orders || 0, fmt(p.revenue || 0)]
      : [i + 1, c.name, c.orders, fmt(c.revenue), fmt(c.net_revenue)];
  });

  return autoT(doc, { head, body: rows, startY: y });
}

function buildProductsSection(doc, annualData, prevData, mode, y) {
  y = sectionTitle(doc, `Top Prodotti / Servizi — Anno ${annualData.year}`, y);
  const prods = annualData.top_products || [];
  const prevMap = {};
  (prevData?.top_products || []).forEach(p => { prevMap[p.description] = p; });

  const head = mode === 'comparison'
    ? [['#', 'Prodotto / Servizio', `Qtà ${annualData.year}`, `Fatturato ${annualData.year}`, `Qtà ${annualData.year - 1}`, `Fatturato ${annualData.year - 1}`]]
    : [['#', 'Prodotto / Servizio', 'Qtà', 'Fatturato', 'Ordini']];

  const rows = prods.map((p, i) => {
    const pp = prevMap[p.description] || {};
    return mode === 'comparison'
      ? [i + 1, p.description, fmtN(p.qty), fmt(p.revenue), fmtN(pp.qty || 0), fmt(pp.revenue || 0)]
      : [i + 1, p.description, fmtN(p.qty), fmt(p.revenue), p.order_count];
  });

  return autoT(doc, { head, body: rows, startY: y });
}

function buildSalesSection(doc, annualData, prevData, mode, y) {
  y = sectionTitle(doc, `Performance Commerciali — Anno ${annualData.year}`, y);
  const reps = annualData.by_salesperson || [];
  const prevMap = {};
  (prevData?.by_salesperson || []).forEach(r => { prevMap[r.name] = r; });

  const head = mode === 'comparison'
    ? [['#', 'Commerciale', `Ordini ${annualData.year}`, `Fatturato ${annualData.year}`, `Ordini ${annualData.year - 1}`, `Fatturato ${annualData.year - 1}`]]
    : [['#', 'Commerciale', 'Ordini', 'Fatturato', 'Medio Ordine']];

  const rows = reps.map((r, i) => {
    const p = prevMap[r.name] || {};
    return mode === 'comparison'
      ? [i + 1, r.name, r.orders, fmt(r.revenue), p.orders || 0, fmt(p.revenue || 0)]
      : [i + 1, r.name, r.orders, fmt(r.revenue), r.orders > 0 ? fmt(r.revenue / r.orders) : '—'];
  });

  return autoT(doc, { head, body: rows, startY: y });
}

function buildLTVSection(doc, cumulativeData, y) {
  y = sectionTitle(doc, 'Valore Lifetime Clienti (LTV) — Storico', y);
  const clients = cumulativeData?.client_lifetime_value || [];
  const rows = clients.map((c, i) => [
    i + 1, c.name, c.orders, fmt(c.revenue), fmt(c.avg_order_value),
    c.first_order ? new Date(c.first_order).toLocaleDateString('it-IT') : '—',
    c.last_order  ? new Date(c.last_order).toLocaleDateString('it-IT')  : '—',
    c.avg_days_between_orders ? `~${c.avg_days_between_orders}gg` : '—',
  ]);

  return autoT(doc, {
    head: [['#', 'Cliente', 'Ordini', 'Tot. Speso', 'Medio Ordine', 'Primo Ord.', 'Ultimo Ord.', 'Freq.']],
    body: rows, startY: y,
    columnStyles: { 0: { cellWidth: 8 }, 3: { halign: 'right' }, 4: { halign: 'right' } },
  });
}

function buildForecastPdfSection(doc, forecastData, year, y) {
  const summaryData  = forecastData?.summary;
  const varianceData = forecastData?.variance || [];
  const tf = summaryData?.total_forecast || 0;
  const ta = summaryData?.total_actual   || 0;
  const tv = ta - tf;
  const tvPct = tf > 0 ? ((tv / tf) * 100).toFixed(1) : null;

  y = sectionTitle(doc, `Forecast & Budget — Anno ${year}`, y);
  y = autoT(doc, {
    head: [['Indicatore', 'Valore']],
    body: [
      ['Budget Totale',     fmt(tf)],
      ['Consuntivo Totale', fmt(ta)],
      ['Varianza (€)',      (tv >= 0 ? '+' : '') + fmt(tv)],
      ['Varianza (%)',      tvPct != null ? `${tv >= 0 ? '+' : ''}${tvPct}%` : 'N/D'],
    ],
    startY: y,
    columnStyles: { 0: { cellWidth: 80 }, 1: { halign: 'right' } },
  });

  const months = summaryData?.months || [];
  if (months.length > 0) {
    if (y > 220) { doc.addPage(); y = 18; }
    y = sectionTitle(doc, `Andamento Mensile Forecast — ${year}`, y);
    y = autoT(doc, {
      head: [['Mese', 'Budget €', 'Consuntivo €', 'Varianza €', 'Var. %']],
      body: months.map(m => {
        const v  = m.variance || 0;
        const pM = m.forecast > 0 ? ((v / m.forecast) * 100).toFixed(1) : null;
        return [
          m.label || String(m.month),
          fmt(m.forecast), fmt(m.actual),
          (v >= 0 ? '+' : '') + fmt(v),
          pM != null ? `${v >= 0 ? '+' : ''}${pM}%` : 'N/D',
        ];
      }),
      startY: y,
    });
  }

  if (varianceData.length > 0) {
    if (y > 200) { doc.addPage(); y = 18; }
    y = sectionTitle(doc, `Varianze per Azienda / Prodotto — ${year}`, y);
    y = autoT(doc, {
      head: [['Azienda', 'Prodotto', 'Budget €', 'Consuntivo €', 'Varianza €', 'Var. %']],
      body: varianceData.map(r => {
        const v = r.variance_amount || 0;
        return [
          r.company_name || '—', r.product_name || '—',
          fmt(r.forecast_amount), fmt(r.actual_amount),
          (v >= 0 ? '+' : '') + fmt(v),
          r.variance_pct != null ? `${r.variance_pct >= 0 ? '+' : ''}${r.variance_pct}%` : 'N/D',
        ];
      }),
      startY: y,
    });
  }

  return y;
}

function ws_forecast(forecastData, year) {
  const summaryData  = forecastData?.summary;
  const varianceData = forecastData?.variance || [];
  const months = summaryData?.months || [];
  const tf = summaryData?.total_forecast || 0;
  const ta = summaryData?.total_actual   || 0;
  const tv = ta - tf;
  const tvPct = tf > 0 ? ((tv / tf) * 100).toFixed(1) : null;

  const rows = [
    [{ v: `Forecast & Budget — Anno ${year}`, t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    [{ v: 'RIEPILOGO KPI', t: 's', s: { font: { bold: true } } }],
    ['Indicatore', 'Valore (€)'],
    ['Budget Totale',     tf],
    ['Consuntivo Totale', ta],
    ['Varianza',          tv],
    ['Varianza %',        tvPct != null ? `${tv >= 0 ? '+' : ''}${tvPct}%` : 'N/D'],
    [],
    [{ v: 'ANDAMENTO MENSILE', t: 's', s: { font: { bold: true } } }],
    ['Mese', 'Budget (€)', 'Consuntivo (€)', 'Varianza (€)', 'Var. (%)'],
    ...months.map(m => {
      const v  = m.variance || 0;
      const pM = m.forecast > 0 ? ((v / m.forecast) * 100).toFixed(1) : null;
      return [m.label || String(m.month), m.forecast || 0, m.actual || 0, v, pM != null ? `${v >= 0 ? '+' : ''}${pM}%` : 'N/D'];
    }),
    [],
    [{ v: 'DETTAGLIO VARIANZE', t: 's', s: { font: { bold: true } } }],
    ['Azienda', 'Prodotto', 'Budget (€)', 'Consuntivo (€)', 'Varianza (€)', 'Var. (%)'],
    ...varianceData.map(r => {
      const v = r.variance_amount || 0;
      return [
        r.company_name || '', r.product_name || '',
        r.forecast_amount || 0, r.actual_amount || 0, v,
        r.variance_pct != null ? `${r.variance_pct >= 0 ? '+' : ''}${r.variance_pct}%` : 'N/D',
      ];
    }),
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

function buildInvoicesSection(doc, invoicesData, y) {
  y = sectionTitle(doc, 'Riepilogo Fatture', y);
  const d = invoicesData || {};
  const rows = [
    ['Fatture Totali', fmtN(d.count || 0), fmt(d.total_invoiced || 0)],
    ['Pagate', fmtN(d.count_paid || 0), fmt(d.total_paid || 0)],
    ['Da Incassare', fmtN((d.count || 0) - (d.count_paid || 0)), fmt(d.total_outstanding || 0)],
    ['Scadute', fmtN(d.count_overdue || 0), fmt(d.total_overdue || 0)],
  ];

  return autoT(doc, {
    head: [['Categoria', 'Conteggio', 'Importo']],
    body: rows, startY: y,
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
  });
}

// ── PDF ENTRY POINT ────────────────────────────────────────────────────────────
export function generatePDF({ sections, annualData, prevAnnualData, cumulativeData, invoicesData, forecastData, inventoryData, productionData, mode, year }) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const title = mode === 'comparison'
    ? `Report Ordini — Confronto ${year} vs ${year - 1}`
    : `Report Ordini — Anno ${year}`;
  const subtitle = `Generato da AGRI-CRM · ${new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}`;

  let y = addHeader(doc, title, subtitle);

  const addSection = (key, buildFn) => {
    if (!sections.includes(key)) return;
    if (y > 250) { doc.addPage(); y = 18; }
    y = buildFn(doc, y);
  };

  if (sections.includes('kpi'))        y = buildKpiSection(doc, annualData, prevAnnualData, mode, y);
  if (sections.includes('monthly'))    { if (y > 220) { doc.addPage(); y = 18; } y = buildMonthlySection(doc, annualData, prevAnnualData, mode, y); }
  if (sections.includes('status'))     addSection('status',  (d, _y) => buildStatusSection(d, annualData, _y));
  if (sections.includes('clients'))    { if (y > 200) { doc.addPage(); y = 18; } y = buildClientsSection(doc, annualData, prevAnnualData, mode, y); }
  if (sections.includes('products'))   { if (y > 200) { doc.addPage(); y = 18; } y = buildProductsSection(doc, annualData, prevAnnualData, mode, y); }
  if (sections.includes('sales'))      { if (y > 200) { doc.addPage(); y = 18; } y = buildSalesSection(doc, annualData, prevAnnualData, mode, y); }
  if (sections.includes('ltv'))        { if (y > 180) { doc.addPage(); y = 18; } y = buildLTVSection(doc, cumulativeData, y); }
  if (sections.includes('invoices'))   addSection('invoices', (d, _y) => buildInvoicesSection(d, invoicesData, _y));
  if (sections.includes('forecast'))   { if (y > 180) { doc.addPage(); y = 18; } y = buildForecastPdfSection(doc, forecastData, year, y); }
  if (sections.includes('inventory'))  { if (y > 180) { doc.addPage(); y = 18; } y = buildInventoryPdfSection(doc, inventoryData, y); }
  if (sections.includes('production')) { if (y > 180) { doc.addPage(); y = 18; } y = buildProductionPdfSection(doc, productionData, year, y); }

  // Footer on each page
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(`Pagina ${i} di ${totalPages} · AGRI-CRM`, 105, 293, { align: 'center' });
  }

  const filename = `AGRI-CRM_Report_${year}${mode === 'comparison' ? `_vs_${year - 1}` : ''}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

// ── EXCEL HELPERS ──────────────────────────────────────────────────────────────
const styledCell = (val, bold = false, bg = null) => {
  const cell = { v: val, t: typeof val === 'number' ? 'n' : 's' };
  if (bold || bg) {
    cell.s = {
      font: bold ? { bold: true } : undefined,
      fill: bg ? { fgColor: { rgb: bg } } : undefined,
    };
  }
  return cell;
};

function ws_kpi(annualData, prevData, mode, year) {
  const s = annualData.summary;
  const p = prevData?.summary || {};
  const rows = [
    [{ v: `KPI — Anno ${year}`, t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    mode === 'comparison'
      ? [{ v: 'Indicatore', t: 's', s: { font: { bold: true } } }, `Anno ${year}`, `Anno ${year - 1}`, 'Variazione %']
      : [{ v: 'Indicatore', t: 's', s: { font: { bold: true } } }, `Anno ${year}`],
    ['Totale Ordini',         s.total_orders,    ...(mode === 'comparison' ? [p.total_orders || 0, pct(s.total_orders, p.total_orders || 0)] : [])],
    ['Fatturato (IVA incl.)', s.total_revenue,   ...(mode === 'comparison' ? [p.total_revenue || 0, pct(s.total_revenue, p.total_revenue || 0)] : [])],
    ['Imponibile Netto',      s.net_revenue,     ...(mode === 'comparison' ? [p.net_revenue || 0, pct(s.net_revenue, p.net_revenue || 0)] : [])],
    ['IVA Totale',            s.tax_amount,      ...(mode === 'comparison' ? [p.tax_amount || 0, pct(s.tax_amount, p.tax_amount || 0)] : [])],
    ['Valore Medio Ordine',   s.avg_order_value, ...(mode === 'comparison' ? [p.avg_order_value || 0, pct(s.avg_order_value, p.avg_order_value || 0)] : [])],
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

function ws_monthly(annualData, prevData, mode, year) {
  const months = annualData.monthly_trend || [];
  const prev   = prevData?.monthly_trend || [];
  const header = mode === 'comparison'
    ? ['Mese', `Fatturato ${year}`, `Imponibile ${year}`, `Ordini ${year}`, `Fatturato ${year - 1}`, `Ordini ${year - 1}`]
    : ['Mese', 'Fatturato', 'Imponibile', 'Ordini'];
  const rows = [
    [{ v: `Andamento Mensile — ${year}`, t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    header,
    ...months.map((m, i) => {
      const pm = prev[i] || {};
      return mode === 'comparison'
        ? [m.month, m.revenue, m.net_revenue, m.orders, pm.revenue || 0, pm.orders || 0]
        : [m.month, m.revenue, m.net_revenue, m.orders];
    }),
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

function ws_status(annualData, year) {
  const status = annualData.summary?.by_status || {};
  const rows = [
    [{ v: `Ordini per Stato — ${year}`, t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    ['Stato', 'Ordini', 'Fatturato'],
    ...Object.entries(status).sort((a, b) => b[1].value - a[1].value)
      .map(([k, v]) => [STATUS_IT[k] || k, v.count, v.value]),
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

function ws_clients(annualData, prevData, mode, year) {
  const clients = annualData.top_clients || [];
  const prevMap = {};
  (prevData?.top_clients || []).forEach(c => { prevMap[c.name] = c; });
  const header = mode === 'comparison'
    ? ['#', 'Cliente', `Ordini ${year}`, `Fatturato ${year}`, `Ordini ${year - 1}`, `Fatturato ${year - 1}`]
    : ['#', 'Cliente', 'Ordini', 'Fatturato', 'Imponibile'];
  const rows = [
    [{ v: `Top Clienti — ${year}`, t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    header,
    ...clients.map((c, i) => {
      const p = prevMap[c.name] || {};
      return mode === 'comparison'
        ? [i + 1, c.name, c.orders, c.revenue, p.orders || 0, p.revenue || 0]
        : [i + 1, c.name, c.orders, c.revenue, c.net_revenue];
    }),
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

function ws_products(annualData, prevData, mode, year) {
  const prods = annualData.top_products || [];
  const prevMap = {};
  (prevData?.top_products || []).forEach(p => { prevMap[p.description] = p; });
  const header = mode === 'comparison'
    ? ['#', 'Prodotto', `Qtà ${year}`, `Fatturato ${year}`, `Qtà ${year - 1}`, `Fatturato ${year - 1}`]
    : ['#', 'Prodotto', 'Qtà', 'Fatturato', 'Ordini'];
  const rows = [
    [{ v: `Top Prodotti — ${year}`, t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    header,
    ...prods.map((p, i) => {
      const pp = prevMap[p.description] || {};
      return mode === 'comparison'
        ? [i + 1, p.description, p.qty, p.revenue, pp.qty || 0, pp.revenue || 0]
        : [i + 1, p.description, p.qty, p.revenue, p.order_count];
    }),
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

function ws_sales(annualData, prevData, mode, year) {
  const reps = annualData.by_salesperson || [];
  const prevMap = {};
  (prevData?.by_salesperson || []).forEach(r => { prevMap[r.name] = r; });
  const header = mode === 'comparison'
    ? ['#', 'Commerciale', `Ordini ${year}`, `Fatturato ${year}`, `Ordini ${year - 1}`, `Fatturato ${year - 1}`]
    : ['#', 'Commerciale', 'Ordini', 'Fatturato', 'Medio Ordine'];
  const rows = [
    [{ v: `Commerciali — ${year}`, t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    header,
    ...reps.map((r, i) => {
      const p = prevMap[r.name] || {};
      return mode === 'comparison'
        ? [i + 1, r.name, r.orders, r.revenue, p.orders || 0, p.revenue || 0]
        : [i + 1, r.name, r.orders, r.revenue, r.orders > 0 ? +(r.revenue / r.orders).toFixed(2) : 0];
    }),
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

function ws_ltv(cumulativeData) {
  const clients = cumulativeData?.client_lifetime_value || [];
  const rows = [
    [{ v: 'Valore Lifetime Clienti (LTV) — Storico', t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    ['#', 'Cliente', 'Ordini', 'Tot. Speso (€)', 'Medio Ordine (€)', 'Primo Ordine', 'Ultimo Ordine', 'Freq. (giorni)'],
    ...clients.map((c, i) => [
      i + 1, c.name, c.orders, c.revenue, c.avg_order_value,
      c.first_order || '—', c.last_order || '—', c.avg_days_between_orders || '—',
    ]),
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

function ws_invoices(invoicesData) {
  const d = invoicesData || {};
  const rows = [
    [{ v: 'Riepilogo Fatture', t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    ['Categoria', 'Conteggio', 'Importo (€)'],
    ['Fatture Totali',  d.count || 0,       d.total_invoiced  || 0],
    ['Pagate',          d.count_paid || 0,  d.total_paid      || 0],
    ['Da Incassare',    (d.count || 0) - (d.count_paid || 0), d.total_outstanding || 0],
    ['Scadute',         d.count_overdue || 0, d.total_overdue || 0],
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

const SECTION_SHEET_MAP = {
  kpi:        (d) => ({ ws: ws_kpi(d.annualData, d.prevAnnualData, d.mode, d.year), name: 'KPI' }),
  monthly:    (d) => ({ ws: ws_monthly(d.annualData, d.prevAnnualData, d.mode, d.year), name: 'Mensile' }),
  status:     (d) => ({ ws: ws_status(d.annualData, d.year), name: 'Per Stato' }),
  clients:    (d) => ({ ws: ws_clients(d.annualData, d.prevAnnualData, d.mode, d.year), name: 'Top Clienti' }),
  products:   (d) => ({ ws: ws_products(d.annualData, d.prevAnnualData, d.mode, d.year), name: 'Top Prodotti' }),
  sales:      (d) => ({ ws: ws_sales(d.annualData, d.prevAnnualData, d.mode, d.year), name: 'Commerciali' }),
  ltv:        (d) => ({ ws: ws_ltv(d.cumulativeData), name: 'LTV Clienti' }),
  invoices:   (d) => ({ ws: ws_invoices(d.invoicesData), name: 'Fatture' }),
  forecast:   (d) => ({ ws: ws_forecast(d.forecastData, d.year), name: 'Forecast & Budget' }),
  inventory:  (d) => ({ ws: ws_inventory(d.inventoryData, d.year), name: 'Scorte' }),
  production: (d) => ({ ws: ws_production(d.productionData, d.year), name: 'Produzione' }),
};

// ── EXCEL ENTRY POINT ──────────────────────────────────────────────────────────
export function generateExcel({ sections, annualData, prevAnnualData, cumulativeData, invoicesData, forecastData, inventoryData, productionData, mode, year }) {
  const wb = XLSX.utils.book_new();

  sections.forEach(key => {
    const builder = SECTION_SHEET_MAP[key];
    if (!builder) return;
    const { ws, name } = builder({ annualData, prevAnnualData, cumulativeData, invoicesData, forecastData, inventoryData, productionData, mode, year });
    XLSX.utils.book_append_sheet(wb, ws, name);
  });

  const filename = `AGRI-CRM_Report_${year}${mode === 'comparison' ? `_vs_${year - 1}` : ''}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, filename);
}


// ══════════════════════════════════════════════════════════════════════════════
// INVENTORY & PRODUCTION REPORT BUILDERS
// ══════════════════════════════════════════════════════════════════════════════

// ── Inventory PDF section ────────────────────────────────────────────────────
function buildInventoryPdfSection(doc, inventoryData, y) {
  const dash = inventoryData?.dashboard || {};
  const materials = inventoryData?.materials || [];

  y = sectionTitle(doc, 'Scorte Magazzino — Riepilogo KPI', y);
  y = autoT(doc, {
    head: [['Indicatore', 'Valore']],
    body: [
      ['Totale materiali',   fmtN(dash.total_materials || 0)],
      ['Sotto scorta (🔴)',  fmtN(dash.alert_red    || 0)],
      ['Scorta bassa (🟡)', fmtN(dash.alert_yellow || 0)],
      ['Scorta OK (🟢)',    fmtN(dash.alert_green  || 0)],
      ['Valore magazzino',   fmt(dash.total_stock_value || 0)],
    ],
    startY: y,
    columnStyles: { 0: { cellWidth: 80 }, 1: { halign: 'right' } },
  });

  if (materials.length > 0) {
    if (y > 210) { doc.addPage(); y = 18; }
    y = sectionTitle(doc, 'Lista Materiali', y);
    y = autoT(doc, {
      head: [['Materiale', 'SKU', 'UdM', 'Giacenza', 'Disponibile', 'Scorta min', 'Costo unit.', 'Fornitore', 'Stato']],
      body: materials.map(m => [
        m.name, m.sku || '—', m.unit,
        fmtQ(m.current_stock), fmtQ(m.available_qty), fmtQ(m.min_stock),
        fmt(m.unit_cost),
        m.supplier || '—',
        ALERT_IT[m.alert] || '—',
      ]),
      startY: y,
      styles: { fontSize: 7 },
      columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
    });
  }

  const criticals = (dash.critical_materials || []);
  if (criticals.length > 0) {
    if (y > 210) { doc.addPage(); y = 18; }
    y = sectionTitle(doc, 'Materiali Critici — Piano Acquisti Consigliato', y);
    y = autoT(doc, {
      head: [['Materiale', 'SKU', 'UdM', 'Giacenza', 'Scorta min', 'Qtà riordino', 'Fornitore', 'Lead time', 'Stato']],
      body: criticals.map(m => [
        m.name, m.sku || '—', m.unit,
        fmtQ(m.current_stock), fmtQ(m.min_stock), fmtQ(m.reorder_qty),
        m.supplier || '—', `${m.lead_time_days || '—'}gg`,
        ALERT_IT[m.alert] || '—',
      ]),
      startY: y,
      styles: { fontSize: 7 },
    });
  }

  return y;
}

// ── Production PDF section ───────────────────────────────────────────────────
function buildProductionPdfSection(doc, productionData, year, y) {
  const consuntivo = productionData?.consuntivo || {};
  const prodSummary = consuntivo.production_summary || [];
  const matCoverage = consuntivo.material_coverage || [];
  const requirements = productionData?.requirements?.requirements || [];

  y = sectionTitle(doc, `Produzione — Pianificato vs Consuntivo (${year})`, y);

  if (prodSummary.length > 0) {
    y = autoT(doc, {
      head: [['Articolo', 'Forecast vendite', 'Venduto reale', 'Pianificato', 'Prodotto', 'Var. %', 'Completam.']],
      body: prodSummary.map(p => [
        p.product_name,
        p.forecast_qty > 0 ? fmtQ(p.forecast_qty, 0) : '—',
        fmtQ(p.actual_sales, 0),
        fmtQ(p.planned_qty, 0),
        fmtQ(p.actual_qty, 0),
        p.variance_pct != null ? `${p.variance_pct >= 0 ? '+' : ''}${p.variance_pct}%` : '—',
        p.completion_pct != null ? `${p.completion_pct}%` : '—',
      ]),
      startY: y,
      styles: { fontSize: 7 },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
    });
  } else {
    y = autoT(doc, { head: [['Info']], body: [['Nessun piano di produzione per questo periodo']], startY: y });
  }

  if (requirements.length > 0) {
    if (y > 210) { doc.addPage(); y = 18; }
    y = sectionTitle(doc, `Fabbisogni Materiali MRP (${year})`, y);
    y = autoT(doc, {
      head: [['Materiale', 'SKU', 'UdM', 'Fabbisogno', 'Disponibile', 'Acquisto suggerito', 'Costo stimato', 'Fornitore', 'Stato']],
      body: requirements.map(r => [
        r.material_name, r.sku || '—', r.material_unit,
        fmtQ(r.total_need, 4), fmtQ(r.available_qty, 4),
        r.suggested_purchase_qty > 0 ? fmtQ(r.suggested_purchase_qty, 4) : '✓ OK',
        r.suggested_cost > 0 ? fmt(r.suggested_cost) : '—',
        r.supplier || '—',
        ALERT_IT[r.alert] || '—',
      ]),
      startY: y,
      styles: { fontSize: 7 },
      columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
    });
  }

  if (matCoverage.length > 0) {
    if (y > 200) { doc.addPage(); y = 18; }
    y = sectionTitle(doc, 'Copertura Scorte — Semaforo', y);
    y = autoT(doc, {
      head: [['Materiale', 'SKU', 'Giacenza', 'Scorta min', 'Cons. 30gg', 'Copertura (gg)', 'Lead time', 'Stato']],
      body: matCoverage.map(m => [
        m.name, m.sku || '—',
        `${fmtQ(m.current_stock, 4)} ${m.unit}`,
        fmtQ(m.min_stock, 4),
        m.consumption_30d > 0 ? fmtQ(m.consumption_30d, 4) : '—',
        m.coverage_days != null ? `${m.coverage_days} gg` : '∞',
        `${m.lead_time_days || '—'} gg`,
        ALERT_IT[m.alert] || '—',
      ]),
      startY: y,
      styles: { fontSize: 7 },
      columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
    });
  }

  return y;
}

// ── Inventory Excel worksheet ────────────────────────────────────────────────
function ws_inventory(inventoryData, year) {
  const dash = inventoryData?.dashboard || {};
  const materials = inventoryData?.materials || [];
  const criticals = dash.critical_materials || [];

  const rows = [
    [{ v: `Scorte Magazzino`, t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    [{ v: 'KPI RIEPILOGO', t: 's', s: { font: { bold: true } } }],
    ['Totale materiali',    dash.total_materials || 0],
    ['Sotto scorta (🔴)',   dash.alert_red    || 0],
    ['Scorta bassa (🟡)',  dash.alert_yellow || 0],
    ['Scorta OK (🟢)',     dash.alert_green  || 0],
    ['Valore magazzino €', dash.total_stock_value || 0],
    [],
    [{ v: 'LISTA MATERIALI', t: 's', s: { font: { bold: true } } }],
    ['Materiale', 'SKU', 'UdM', 'Giacenza', 'Disponibile', 'Scorta min', 'Qtà riordino', 'Costo unit. €', 'Valore stock €', 'Fornitore', 'Lead time (gg)', 'Stato'],
    ...materials.map(m => [
      m.name, m.sku || '', m.unit,
      m.current_stock || 0, m.available_qty || 0, m.min_stock || 0, m.reorder_qty || 0,
      m.unit_cost || 0,
      +((m.current_stock || 0) * (m.unit_cost || 0)).toFixed(2),
      m.supplier || '', m.lead_time_days || 0,
      ALERT_IT[m.alert] || '',
    ]),
    [],
    [{ v: 'MATERIALI CRITICI — PIANO ACQUISTI', t: 's', s: { font: { bold: true } } }],
    ['Materiale', 'SKU', 'UdM', 'Giacenza', 'Scorta min', 'Qtà riordino', 'Fornitore', 'Lead time (gg)', 'Stato'],
    ...criticals.map(m => [
      m.name, m.sku || '', m.unit,
      m.current_stock || 0, m.min_stock || 0, m.reorder_qty || 0,
      m.supplier || '', m.lead_time_days || 0,
      ALERT_IT[m.alert] || '',
    ]),
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}

// ── Production Excel worksheet ───────────────────────────────────────────────
function ws_production(productionData, year) {
  const consuntivo = productionData?.consuntivo || {};
  const prodSummary = consuntivo.production_summary || [];
  const matCoverage = consuntivo.material_coverage || [];
  const requirements = productionData?.requirements?.requirements || [];

  const rows = [
    [{ v: `Produzione — Anno ${year}`, t: 's', s: { font: { bold: true, sz: 12 } } }],
    [],
    [{ v: 'PIANIFICATO VS CONSUNTIVO', t: 's', s: { font: { bold: true } } }],
    ['Articolo', 'Forecast vendite', 'Venduto reale', 'Pianificato', 'Prodotto', 'Varianza', 'Var. %', 'Completamento %'],
    ...prodSummary.map(p => [
      p.product_name,
      p.forecast_qty || 0, p.actual_sales || 0,
      p.planned_qty || 0, p.actual_qty || 0,
      p.variance_qty || 0,
      p.variance_pct != null ? `${p.variance_pct >= 0 ? '+' : ''}${p.variance_pct}%` : 'N/D',
      p.completion_pct != null ? `${p.completion_pct}%` : 'N/D',
    ]),
    [],
    [{ v: 'FABBISOGNI MATERIALI (MRP)', t: 's', s: { font: { bold: true } } }],
    ['Materiale', 'SKU', 'UdM', 'Fabbisogno', 'Disponibile', 'Acquisto suggerito', 'Costo stimato €', 'Fornitore', 'Stato'],
    ...requirements.map(r => [
      r.material_name, r.sku || '', r.material_unit,
      r.total_need || 0, r.available_qty || 0,
      r.suggested_purchase_qty || 0, r.suggested_cost || 0,
      r.supplier || '', ALERT_IT[r.alert] || '',
    ]),
    [],
    [{ v: 'COPERTURA SCORTE', t: 's', s: { font: { bold: true } } }],
    ['Materiale', 'SKU', 'UdM', 'Giacenza', 'Scorta min', 'Consumo 30gg', 'Cons./giorno', 'Copertura (gg)', 'Lead time (gg)', 'Stato'],
    ...matCoverage.map(m => [
      m.name, m.sku || '', m.unit,
      m.current_stock || 0, m.min_stock || 0,
      m.consumption_30d || 0, m.avg_daily_consumption || 0,
      m.coverage_days != null ? m.coverage_days : '∞',
      m.lead_time_days || 0, ALERT_IT[m.alert] || '',
    ]),
  ];
  return XLSX.utils.aoa_to_sheet(rows);
}


// ══════════════════════════════════════════════════════════════════════════════
// FORECAST REPORT GENERATORS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Generate a PDF report for Forecast & Budget data.
 * @param {{ summaryData: object, varianceData: array, year: number, isAdmin: boolean }} args
 */
export function generateForecastPDF({ summaryData, varianceData, year, isAdmin }) {
  const doc = new jsPDF('portrait', 'mm', 'a4');
  let y = addHeader(
    doc,
    `Report Forecast & Budget — ${year}`,
    `Generato il ${new Date().toLocaleDateString('it-IT')}`,
  );

  // ── KPI Summary ─────────────────────────────────────────────────────────────
  y = sectionTitle(doc, 'Riepilogo KPI', y);
  const tf = summaryData?.total_forecast || 0;
  const ta = summaryData?.total_actual   || 0;
  const tv = ta - tf;
  const tvPct = tf > 0 ? ((tv / tf) * 100).toFixed(1) : null;

  y = autoT(doc, {
    head: [['Indicatore', 'Valore']],
    body: [
      ['Budget Totale',          fmt(tf)],
      ['Consuntivo Totale',      fmt(ta)],
      ['Varianza (€)',           (tv >= 0 ? '+' : '') + fmt(tv)],
      ['Varianza (%)',           tvPct != null ? `${tv >= 0 ? '+' : ''}${tvPct}%` : 'N/D'],
    ],
    startY: y,
  });

  // ── Monthly Breakdown ────────────────────────────────────────────────────────
  const months = summaryData?.months || [];
  if (months.length > 0) {
    y = sectionTitle(doc, 'Andamento Mensile', y);
    y = autoT(doc, {
      head: [['Mese', 'Budget €', 'Consuntivo €', 'Varianza €', 'Var. %']],
      body: months.map(m => {
        const v   = m.variance || 0;
        const pM  = m.forecast > 0 ? ((v / m.forecast) * 100).toFixed(1) : null;
        return [
          m.label || String(m.month),
          fmt(m.forecast),
          fmt(m.actual),
          (v >= 0 ? '+' : '') + fmt(v),
          pM != null ? `${v >= 0 ? '+' : ''}${pM}%` : 'N/D',
        ];
      }),
      startY: y,
    });
  }

  // ── Variance Detail ──────────────────────────────────────────────────────────
  if (varianceData?.length > 0) {
    if (y > 210) { doc.addPage(); y = 18; }
    y = sectionTitle(doc, 'Dettaglio Varianze per Azienda / Prodotto', y);
    const head = isAdmin
      ? [['Azienda', 'Prodotto', 'Commerciale', 'Budget €', 'Consuntivo €', 'Varianza €', 'Var. %']]
      : [['Azienda', 'Prodotto', 'Budget €', 'Consuntivo €', 'Varianza €', 'Var. %']];
    y = autoT(doc, {
      head,
      body: varianceData.map(r => {
        const v = r.variance_amount || 0;
        return [
          r.company_name   || '—',
          r.product_name   || '—',
          ...(isAdmin ? [r.assigned_to_name || '—'] : []),
          fmt(r.forecast_amount),
          fmt(r.actual_amount),
          (v >= 0 ? '+' : '') + fmt(v),
          r.variance_pct != null ? `${r.variance_pct >= 0 ? '+' : ''}${r.variance_pct}%` : 'N/D',
        ];
      }),
      startY: y,
    });
  }

  // ── Footer ───────────────────────────────────────────────────────────────────
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `AGRI-CRM — Report Forecast & Budget ${year} — Pag. ${i} di ${pages}`,
      105, 290, { align: 'center' },
    );
  }

  doc.save(`AGRI-CRM_Forecast_${year}_${new Date().toISOString().slice(0, 10)}.pdf`);
}


/**
 * Generate an Excel workbook for Forecast & Budget data.
 * @param {{ summaryData: object, varianceData: array, year: number, isAdmin: boolean }} args
 */
export function generateForecastExcel({ summaryData, varianceData, year, isAdmin }) {
  const wb = XLSX.utils.book_new();

  // Sheet 1 — KPI
  const tf = summaryData?.total_forecast || 0;
  const ta = summaryData?.total_actual   || 0;
  const tv = ta - tf;
  const tvPct = tf > 0 ? ((tv / tf) * 100).toFixed(1) : null;
  const wsKpi = XLSX.utils.aoa_to_sheet([
    ['Indicatore', 'Valore (€)'],
    ['Budget Totale',     tf],
    ['Consuntivo Totale', ta],
    ['Varianza',          tv],
    ['Varianza %',        tvPct != null ? `${tv >= 0 ? '+' : ''}${tvPct}%` : 'N/D'],
  ]);
  XLSX.utils.book_append_sheet(wb, wsKpi, 'KPI');

  // Sheet 2 — Mensile
  const months = summaryData?.months || [];
  const wsMensile = XLSX.utils.aoa_to_sheet([
    ['Mese', 'Budget (€)', 'Consuntivo (€)', 'Varianza (€)', 'Var. (%)'],
    ...months.map(m => {
      const v  = m.variance || 0;
      const pM = m.forecast > 0 ? ((v / m.forecast) * 100).toFixed(1) : null;
      return [
        m.label || String(m.month),
        m.forecast  || 0,
        m.actual    || 0,
        v,
        pM != null ? `${v >= 0 ? '+' : ''}${pM}%` : 'N/D',
      ];
    }),
  ]);
  XLSX.utils.book_append_sheet(wb, wsMensile, 'Mensile');

  // Sheet 3 — Varianze
  const varHeader = isAdmin
    ? ['Azienda', 'Prodotto', 'Commerciale', 'Budget (€)', 'Consuntivo (€)', 'Varianza (€)', 'Var. (%)']
    : ['Azienda', 'Prodotto', 'Budget (€)', 'Consuntivo (€)', 'Varianza (€)', 'Var. (%)'];
  const wsVar = XLSX.utils.aoa_to_sheet([
    varHeader,
    ...(varianceData || []).map(r => {
      const v = r.variance_amount || 0;
      return [
        r.company_name    || '',
        r.product_name    || '',
        ...(isAdmin ? [r.assigned_to_name || ''] : []),
        r.forecast_amount || 0,
        r.actual_amount   || 0,
        v,
        r.variance_pct != null ? `${r.variance_pct >= 0 ? '+' : ''}${r.variance_pct}%` : 'N/D',
      ];
    }),
  ]);
  XLSX.utils.book_append_sheet(wb, wsVar, 'Varianze');

  XLSX.writeFile(wb, `AGRI-CRM_Forecast_${year}_${new Date().toISOString().slice(0, 10)}.xlsx`);
}
